// App-level data context: a URL-keyed response cache with in-flight dedupe, plus
// board scroll position and a small session memory for UI state a screen
// wants back when the reader returns to it in-app (the home's list ⇄
// briefing tab — never a URL param, and a reload starts it over because
// this memory starts over too). This is what makes back-navigation paint
// instantly (Robinhood feel) without a query library — the pinned stack is
// Context + hooks.
import { createContext, useContext, useMemo, useRef } from "react";

const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  const cacheRef = useRef(new Map()); // key → response data
  const inflightRef = useRef(new Map()); // key → Promise
  const scrollRef = useRef({ board: 0 });
  const memoryRef = useRef(new Map()); // key → remembered UI state

  const value = useMemo(
    () => ({
      getCached: (key) => cacheRef.current.get(key),
      // Single-flight fetch: concurrent callers of the same key share one request.
      fetchCached: (key, fetcher) => {
        if (cacheRef.current.has(key)) {
          return Promise.resolve(cacheRef.current.get(key));
        }
        if (inflightRef.current.has(key)) {
          return inflightRef.current.get(key);
        }
        const promise = fetcher()
          .then((data) => {
            cacheRef.current.set(key, data);
            return data;
          })
          .finally(() => inflightRef.current.delete(key));
        inflightRef.current.set(key, promise);
        return promise;
      },
      invalidate: (key) => cacheRef.current.delete(key),
      // a local write into the cache (an optimistic mutation — a deleted
      // row leaves at once; the next refetch resyncs from the server)
      setCached: (key, data) => {
        cacheRef.current.set(key, data);
      },
      saveScroll: (screen, y) => {
        scrollRef.current[screen] = y;
      },
      getScroll: (screen) => scrollRef.current[screen] || 0,
      // session memory: survives in-app navigation, not a reload
      remember: (key, value) => {
        memoryRef.current.set(key, value);
      },
      recall: (key) => memoryRef.current.get(key),
      // logout (contexts/SessionContext): everything above starts over —
      // the next workspace must never see the last one's cached responses
      // or remembered views
      reset: () => {
        cacheRef.current.clear();
        inflightRef.current.clear();
        memoryRef.current.clear();
        scrollRef.current = { board: 0 };
      },
    }),
    [],
  );

  return (
    <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be used inside <AppDataProvider>");
  return ctx;
}
